/**
 * Stream D — D71-D76: Tenant Migration & Database Management
 *
 * Admin tooling for tenant database operations:
 * - Tier migration (shared→dedicated, Hetzner→Neon, Neon→Hetzner)
 * - Postgres version upgrades
 * - Schema migration rollout
 * - Data export/import (GDPR portability)
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";
import { Database, ArrowRight, Shield, Download, Upload, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface TenantDb {
  tenantId: string;
  tenantName: string;
  tier: "tier1_hetzner" | "tier2_shared" | "tier3_neon";
  dbSize: string;
  pgVersion: string;
  schemaVersion: string;
  lastMigration: string | null;
  status: "healthy" | "migrating" | "error";
}

interface MigrationJob {
  id: string;
  tenantId: string;
  type: "tier_upgrade" | "tier_downgrade" | "pg_upgrade" | "schema_rollout" | "data_export" | "data_import";
  status: "pending" | "running" | "completed" | "failed";
  progress: number;
  startedAt: string;
  error: string | null;
}

export default function TenantMigrationPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantDb[]>([]);
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [isActioning, setIsActioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [tenantsRes, jobsRes] = await Promise.all([
        apiFetch<{ data: TenantDb[] }>("/admin/tenants/databases"),
        apiFetch<{ data: MigrationJob[] }>("/admin/migrations"),
      ]);
      setTenants(tenantsRes.data);
      setJobs(jobsRes.data);
    } catch { setTenants([]); setJobs([]); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startMigration = async (tenantId: string, type: string, targetTier?: string) => {
    const confirmed = window.confirm(`${t(MSG.admin.tenantMigration.confirmPrefix)} ${type.replace(/_/g, " ")} ${t(MSG.admin.tenantMigration.confirmSuffix)}`);
    if (!confirmed) return;
    setIsActioning(true);
    setError(null);
    try {
      await apiFetch("/admin/migrations", {
        method: "POST",
        body: JSON.stringify({ tenantId, type, targetTier }),
      });
      await fetchData();
    } catch { setError(t(MSG.admin.tenantMigration.startError)); }
    finally { setIsActioning(false); }
  };

  const handleExport = async (tenantId: string) => {
    setIsActioning(true);
    try {
      const res = await apiFetch<{ data: { downloadUrl: string } }>(`/admin/tenants/${tenantId}/export`, { method: "POST" });
      window.location.href = res.data.downloadUrl;
    } catch { setError(t(MSG.admin.tenantMigration.exportError)); }
    finally { setIsActioning(false); }
  };

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" />{[1,2,3,4].map(i => <SkeletonCard key={i} className="h-16" />)}</div>;

  const TIER_LABELS = {
    tier1_hetzner: t(MSG.admin.tenantMigration.tierLabels.tier1Hetzner),
    tier2_shared: t(MSG.admin.tenantMigration.tierLabels.tier2Shared),
    tier3_neon: t(MSG.admin.tenantMigration.tierLabels.tier3Neon),
  } as const;
  const TIER_COLORS = { tier1_hetzner: "text-[var(--color-primary)]", tier2_shared: "text-[var(--color-warning)]", tier3_neon: "text-[var(--color-ai)]" } as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3"><Database className="h-5 w-5 text-[var(--color-primary)]" /><div><h1 className="text-2xl font-semibold text-[var(--color-foreground)]">{t(MSG.admin.tenantMigration.title)}</h1><p className="text-sm text-[var(--color-muted-foreground)]">{t(MSG.admin.tenantMigration.description)}</p></div></div>

      {error && <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">{error}</div>}

      {/* Active migrations */}
      {jobs.filter(j => j.status === "running" || j.status === "pending").length > 0 && (
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4">
          <h3 className="font-medium text-[var(--color-warning)] mb-2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t(MSG.admin.tenantMigration.activeMigrations)}</h3>
          {jobs.filter(j => j.status === "running" || j.status === "pending").map(j => (
            <div key={j.id} className="flex items-center justify-between py-2">
              <span className="text-sm text-[var(--color-foreground)]">{j.type.replace(/_/g, " ")} — {j.tenantId.slice(0,8)}</span>
              <div className="flex items-center gap-2"><div className="w-24 h-2 rounded-full bg-[var(--color-surface)] overflow-hidden"><div className="h-full rounded-full bg-[var(--color-warning)] transition-all" style={{width:`${j.progress}%`}} /></div><span className="text-xs text-[var(--color-muted-foreground)]" style={{fontVariantNumeric:"tabular-nums"}}>{j.progress}%</span></div>
            </div>
          ))}
        </div>
      )}

      {/* Tenant list */}
      <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
            <th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.tenant)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.tier)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.dbSize)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.pg)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.schema)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.status)}</th><th className="px-4 py-3">{t(MSG.admin.tenantMigration.table.actions)}</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {tenants.map(tenant => (
              <tr key={tenant.tenantId} className="stagger-item">
                <td className="px-4 py-3 font-medium text-[var(--color-foreground)]">{tenant.tenantName}<span className="block text-[10px] text-[var(--color-muted-foreground)] font-mono">{tenant.tenantId.slice(0,12)}</span></td>
                <td className={`px-4 py-3 text-xs font-medium ${TIER_COLORS[tenant.tier]}`}>{TIER_LABELS[tenant.tier]}</td>
                <td className="px-4 py-3 text-[var(--color-muted-foreground)]" style={{fontVariantNumeric:"tabular-nums"}}>{tenant.dbSize}</td>
                <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{tenant.pgVersion}</td>
                <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{tenant.schemaVersion}</td>
                <td className="px-4 py-3">{tenant.status === "healthy" ? <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" /> : tenant.status === "migrating" ? <Loader2 className="h-4 w-4 text-[var(--color-warning)] animate-spin" /> : <AlertTriangle className="h-4 w-4 text-[var(--color-error)]" />}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {tenant.tier === "tier2_shared" && <button type="button" onClick={() => startMigration(tenant.tenantId, "tier_upgrade", "tier1_hetzner")} disabled={isActioning} className="text-[10px] text-[var(--color-primary)] hover:underline">{t(MSG.admin.tenantMigration.actions.tier2ToTier1)}</button>}
                    {tenant.tier === "tier1_hetzner" && <button type="button" onClick={() => startMigration(tenant.tenantId, "tier_upgrade", "tier3_neon")} disabled={isActioning} className="text-[10px] text-[var(--color-ai)] hover:underline">{t(MSG.admin.tenantMigration.actions.tier1ToTier3)}</button>}
                    {tenant.tier === "tier3_neon" && <button type="button" onClick={() => startMigration(tenant.tenantId, "tier_downgrade", "tier1_hetzner")} disabled={isActioning} className="text-[10px] text-[var(--color-warning)] hover:underline">{t(MSG.admin.tenantMigration.actions.tier3ToTier1)}</button>}
                    <button type="button" onClick={() => handleExport(tenant.tenantId)} disabled={isActioning} className="text-[10px] text-[var(--color-muted-foreground)] hover:underline"><Download className="inline h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-[var(--color-surface)] p-3">
        <Shield className="h-4 w-4 text-[var(--color-muted-foreground)] mt-0.5 shrink-0" />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          {t(MSG.admin.tenantMigration.portabilityNotice)}
        </p>
      </div>
    </div>
  );
}
